import { Handle, Position } from "@xyflow/react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../ui/styles/classNames";
import { CARD, type CardGeometry, type Point, type PortRow } from "../model/cardGeometry";
import { isPlacementNode, type GraphNode, type GraphPort, type PortNeed } from "../model/workflowGraph";
import { THEN_HANDLES } from "./flowElements";
import { portFacts, portTooltip, portWords } from "./nodeText";

/**
 * A card's named ports, each at the place its geometry gives (`cardGeometry`): inputs
 * filled from above as tabs on the top edge, inputs filled by an earlier step and outputs
 * as rows on the sides, an input's outputs as dots on its bottom edge. Each port is a
 * React Flow handle, so its edge belongs to it; the handle holds the port's visible mark
 * (`PortMark`), which the legend draws too. Every port shows its purpose and data type in
 * words; an input's outline says whether the step needs it (solid: required, dotted:
 * optional), and an output no later step uses is hollow (F40, R-10). `data-port` marks the
 * visible port for the browser check that edges end on their ports.
 */
/** `connectable`: in the editor, a binding is dragged from an output part to an input slot. */
export function NodePorts({ node, geometry, connectable = false }: { node: GraphNode; geometry: CardGeometry; connectable?: boolean }) {
  return (
    <>
      <ThenHandles width={geometry.width} y={geometry.thenY} />
      {geometry.left.map((row) => (
        <SideRow key={row.port.id} node={node} row={row} side="left" cardWidth={geometry.width} connectable={connectable} />
      ))}
      {geometry.right.map((row) => (
        <SideRow key={row.port.id} node={node} row={row} side="right" cardWidth={geometry.width} connectable={connectable} />
      ))}
      {geometry.tabs.map((tab) => (
        <TopTab key={tab.port.id} node={node} port={tab.port} x={tab.x} width={tab.width} connectable={connectable} />
      ))}
      {geometry.bottom.map((dot) => (
        <BottomDot key={dot.port.id} node={node} port={dot.port} at={{ x: dot.x, y: geometry.height }} connectable={connectable} />
      ))}
    </>
  );
}

/** Outputs of an open frame, on its bottom edge. */
export function FramePorts({ node, anchors }: { node: GraphNode; anchors: ReadonlyMap<string, Point> }) {
  return (
    <>
      {node.ports.map((port) => {
        const at = anchors.get(port.id);
        return at === undefined ? null : <BottomDot key={port.id} node={node} port={port} at={at} />;
      })}
    </>
  );
}

/** Where `then` arrows meet the card: its sides at header height. Not drawn. */
export function ThenHandles({ width, y }: { width: number; y: number }) {
  return (
    <>
      <Handle type="target" id={THEN_HANDLES.in} position={Position.Left} isConnectable={false} className="app-flow-handle" style={at({ x: 0, y })} />
      <Handle type="source" id={THEN_HANDLES.out} position={Position.Right} isConnectable={false} className="app-flow-handle" style={at({ x: width, y })} />
    </>
  );
}

/**
 * What a port's circle shows: for an input, whether the step needs it; for an output,
 * whether a later step uses it, and whether it is read from the band above (an input
 * card's output). Connection state in a run ("Missing", "Waiting for step 2") is not a
 * mark: the Thread view shows it as a labelled badge.
 */
export type PortMarkKind = { readonly direction: "input"; readonly need: PortNeed } | { readonly direction: "output"; readonly used: boolean; readonly read: boolean };

function portMarkOf(port: GraphPort, read: boolean): PortMarkKind {
  return port.direction === "input" ? { direction: "input", need: port.need ?? "undeclared" } : { direction: "output", used: port.connected, read };
}

/** A port's circle, on a card and in the legend. */
export function PortMark({ mark }: { mark: PortMarkKind }) {
  const className =
    mark.direction === "input"
      ? classNames("app-flow-port-mark app-flow-port-input", `app-flow-port-${mark.need}`)
      : classNames("app-flow-port-mark app-flow-port-output", mark.read ? "app-flow-port-read" : "", mark.used ? "" : "app-flow-port-hollow");
  return <span className={className} aria-hidden="true" />;
}

/** An input filled from the band above: a tab, outlined as its need says, on a card and in the legend. */
export function InputTab({ need, style, title, port, children }: { need: PortNeed; style?: CSSProperties; title?: string; port?: string; children?: ReactNode }) {
  return (
    <span className={classNames("app-flow-tab", `app-flow-tab-${need}`)} style={style} title={title} data-port={port}>
      {children}
    </span>
  );
}

/** A port's data type in words, set apart from its purpose; on a card and in the legend. */
export function PortType({ text }: { text: string }) {
  return <span className="app-flow-port-type">{text}</span>;
}

/** "Candidate claims · Annotations": the type whole, the purpose as far as it fits. */
function PortWords({ node, port }: { node: GraphNode; port: GraphPort }) {
  const { t } = useTranslation();
  const { purpose, type } = portWords(node, port, t);
  return (
    <>
      <span className="app-flow-port-name truncate">{purpose}</span>
      {type === null ? null : (
        <span className="app-flow-port-type-slot whitespace-nowrap">
          <span aria-hidden="true">·</span> <PortType text={type} />
        </span>
      )}
    </>
  );
}

/** One side port's row; its label's width is the geometry's (`PortRow.width`). */
function SideRow({ node, row, side, cardWidth, connectable }: { node: GraphNode; row: PortRow; side: "left" | "right"; cardWidth: number; connectable: boolean }) {
  const { port, y } = row;
  const { t } = useTranslation();
  const style: CSSProperties = {
    top: y - CARD.portRow / 2,
    height: CARD.portRow,
    width: row.width,
    ...(side === "left" ? { left: 0, paddingLeft: CARD.paddingX } : { right: 0, paddingRight: CARD.paddingX }),
  };
  const tooltip = portTooltip(node, port, t);
  return (
    <>
      <div className={classNames("app-flow-port-row", side === "right" ? "justify-end" : "", port.direction === "output" && !port.connected ? "app-flow-port-unused" : "")} style={style} title={tooltip}>
        <PortWords node={node} port={port} />
        <span className="sr-only">{`; ${portFacts(port, t)}`}</span>
      </div>
      <Handle
        type={port.direction === "input" ? "target" : "source"}
        id={port.id}
        position={side === "left" ? Position.Left : Position.Right}
        isConnectable={connectable && port.bindable}
        className={classNames("app-flow-port-anchor", connectable && port.bindable ? "app-flow-port-bindable" : "")}
        style={at({ x: side === "left" ? 0 : cardWidth, y })}
        title={tooltip}
        data-port={port.id}
      >
        <PortMark mark={portMarkOf(port, false)} />
      </Handle>
    </>
  );
}

function TopTab({ node, port, x, width, connectable }: { node: GraphNode; port: GraphPort; x: number; width: number; connectable: boolean }) {
  const { t } = useTranslation();
  const tooltip = portTooltip(node, port, t);
  return (
    <InputTab need={port.need ?? "undeclared"} style={{ left: x - width / 2, top: -CARD.tab.rise, width, height: CARD.tab.height }} title={tooltip} port={port.id}>
      <PortWords node={node} port={port} />
      <span className="sr-only">{`; ${portFacts(port, t)}`}</span>
      <Handle
        type="target"
        id={port.id}
        position={Position.Top}
        isConnectable={connectable && port.bindable}
        className={classNames("app-flow-handle", connectable && port.bindable ? "app-flow-port-bindable" : "")}
        style={at({ x: width / 2, y: 0 })}
      />
    </InputTab>
  );
}

function BottomDot({ node, port, at: point, connectable = false }: { node: GraphNode; port: GraphPort; at: Point; connectable?: boolean }) {
  const { t } = useTranslation();
  // A source's one output needs no words: its card says its type (F61); a sub-workflow's exports need their names and types.
  const named = isPlacementNode(node);
  const words = named ? <PortWords node={node} port={port} /> : null;
  return (
    <>
      <Handle
        type="source"
        id={port.id}
        position={Position.Bottom}
        isConnectable={connectable && port.bindable}
        className={classNames("app-flow-port-anchor", connectable && port.bindable ? "app-flow-port-bindable" : "")}
        style={at(point)}
        title={portTooltip(node, port, t)}
        data-port={port.id}
      >
        <PortMark mark={portMarkOf(port, true)} />
      </Handle>
      {words === null ? null : (
        <span className="app-flow-port-below" style={{ left: point.x + 8, top: point.y + 2 }}>
          {words}
        </span>
      )}
    </>
  );
}

/** A handle centred on `point`, relative to the card; React Flow's own placement is undone. */
function at(point: Point): CSSProperties {
  return { left: point.x, top: point.y, right: "auto", bottom: "auto", transform: "translate(-50%, -50%)" };
}

